import { useMemo } from 'react';
/* Barras são HTML, não recharts: horizontais, com o rótulo inteiro à esquerda e o
   valor à direita. Nome de pessoa num eixo vertical de gráfico de barras vira
   texto cortado ou girado. */
import {
    Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { LuHash, LuTable, LuChartNoAxesColumn, LuChartLine, LuChartPie } from 'react-icons/lu';
import { useChartTokens, useTooltipStyle } from './ChartKit';
import { useCelular } from '../hooks/useCelular';
import { useAltura } from '../hooks/useAltura';
import {
    ehNumerica, formatarValor,
    type ResultadoRelatorio, type VisualRelatorio,
} from '../services/RelatorioPersonalizadoService';

/* ─── A regra desta tela ──────────────────────────────────────────

   NENHUM FORMATO DESCARTA DADO. Se o resultado tem cinco linhas, todo formato
   mostra as cinco: Número vira cinco cartões, Tabela cinco linhas, Barras cinco
   barras. O que muda entre eles é a forma, nunca a quantidade de informação.

   Foi exatamente isso que quebrou na primeira versão: com quebra por Situação e
   visual "Número", o servidor devolvia cinco linhas e a tela mostrava 69 — o
   total —, jogando fora o que o usuário tinha pedido. A prévia ainda dizia
   "5 linhas" ao lado do número, contradizendo a si mesma.

   Quando um formato realmente não serve para a forma do resultado, ele aparece
   DESABILITADO com o motivo escrito, e nunca some: sumir esconderia que a opção
   existe.                                                                     */

/**
 * Como o servidor rotula a linha cujo valor da separação não existe — um negócio
 * sem data de fechamento, um sem vendedor. Comparar pelo texto é frágil; o certo
 * seria a API marcar a linha. Registrado como dívida no documento da reorganização.
 */
const SEM_DATA = 'Não informado';

export interface FormatoDisponivel {
    id: VisualRelatorio;
    rotulo: string;
    icone: React.ReactNode;
    /** Quando presente, o formato não serve para este resultado — e este é o porquê. */
    impedimento?: string;
}

/**
 * Quais formatos servem para ESTE resultado. Tudo é decidido pela forma dos
 * dados que voltaram, não pela especificação: o componente não precisa saber
 * como a pergunta foi montada.
 */
export function formatosDisponiveis(resultado: ResultadoRelatorio | null): FormatoDisponivel[] {
    const dimensoes = resultado?.colunas.filter(c => !ehNumerica(c.tipo)) ?? [];
    const medidas = resultado?.colunas.filter(c => ehNumerica(c.tipo)) ?? [];
    const primeiraEhTempo = resultado?.colunas[0]?.tipo === 'tempo';

    return [
        { id: 'numero', rotulo: 'Número', icone: <LuHash size={13} /> },
        { id: 'tabela', rotulo: 'Tabela', icone: <LuTable size={13} /> },
        {
            id: 'barras', rotulo: 'Barras', icone: <LuChartNoAxesColumn size={13} />,
            impedimento: dimensoes.length === 0
                ? 'Sem separação, existe um valor só — e uma barra sozinha não compara nada.'
                : undefined,
        },
        {
            id: 'linha', rotulo: 'Linha', icone: <LuChartLine size={13} />,
            impedimento: primeiraEhTempo
                ? undefined
                : 'A linha mostra evolução, então a primeira separação precisa ser por tempo (dia, mês ou ano).',
        },
        {
            id: 'pizza', rotulo: 'Pizza', icone: <LuChartPie size={13} />,
            impedimento: dimensoes.length === 1 && medidas.length > 0
                ? undefined
                : dimensoes.length === 0
                    ? 'A pizza mostra a composição de um total, então precisa de uma separação.'
                    : 'Com duas separações a pizza mente: as fatias deixam de somar o todo.',
        },
    ];
}

/** Piso para a janela baixa: abaixo disto o eixo e a curva se atropelam. */
const ALTURA_MINIMA_GRAFICO = 240;

interface Props {
    resultado: ResultadoRelatorio;
    visual: VisualRelatorio;
    /**
     * Piso da altura do gráfico. O padrão vale para a página inteira; dentro de
     * um bloco do painel o piso tem que ser menor que o bloco, senão o gráfico
     * transborda e a caixa ganha rolagem — que num gráfico não serve para nada,
     * já que a curva cortada não volta rolando.
     */
    alturaMinima?: number;
}

export default function RelatorioResultado({ resultado, visual, alturaMinima = ALTURA_MINIMA_GRAFICO }: Props) {
    const celular = useCelular();
    const tokens = useChartTokens();
    const tooltip = useTooltipStyle();
    const [refGrafico, alturaGrafico] = useAltura<HTMLDivElement>();

    const iDimensoes = useMemo(
        () => resultado.colunas.map((c, i) => ({ c, i })).filter(x => !ehNumerica(x.c.tipo)).map(x => x.i),
        [resultado],
    );
    const iMedidas = useMemo(
        () => resultado.colunas.map((c, i) => ({ c, i })).filter(x => ehNumerica(x.c.tipo)).map(x => x.i),
        [resultado],
    );

    const temDimensao = iDimensoes.length > 0;
    const iPrimeiraMedida = iMedidas[0] ?? -1;

    /** Dados no formato que o recharts espera, com o rótulo já pronto. */
    const serie = useMemo(() => resultado.linhas.map(linha => {
        const ponto: Record<string, string | number> = {
            rotulo: iDimensoes.map(i => String(linha[i] ?? '—')).join(' · '),
        };
        iMedidas.forEach(i => { ponto[resultado.colunas[i].id] = Number(linha[i] ?? 0); });
        return ponto;
    }), [resultado, iDimensoes, iMedidas]);

    const eixoY = (indice: number) => (valor: number) => {
        const tipo = indice >= 0 ? resultado.colunas[indice].tipo : 'inteiro';
        if (tipo === 'percentual') return `${Math.round(valor * 100)}%`;
        if (Math.abs(valor) >= 1000) return `${Math.round(valor / 1000)}k`;
        return String(valor);
    };

    const formatarTooltip = (valor: number | string, nome: string) => {
        const coluna = resultado.colunas.find(c => c.id === nome || c.nome === nome);
        return [formatarValor(valor as number, coluna?.tipo ?? 'inteiro'), coluna?.nome ?? nome];
    };

    /* ─── Número ─────────────────────────────────────────────────
       Sem separação: um número por medida, grande.
       COM separação: um cartão por linha — é aqui que a regra vive. */
    if (visual === 'numero') {
        if (!temDimensao) {
            return (
                <div className="rl-numeros">
                    {iMedidas.map(i => (
                        <div key={resultado.colunas[i].id} className="rl-numero">
                            <span className="rl-numero-l">{resultado.colunas[i].nome}</span>
                            <strong className="rl-numero-v">
                                {formatarValor(resultado.totais[i] ?? resultado.linhas[0]?.[i] ?? null, resultado.colunas[i].tipo)}
                            </strong>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="rl-cartoes">
                {resultado.linhas.map((linha, n) => (
                    <div key={n} className="rl-cartao">
                        <span className="rl-cartao-l">{iDimensoes.map(i => linha[i] ?? '—').join(' · ')}</span>
                        <strong className="rl-cartao-v">
                            {formatarValor(linha[iPrimeiraMedida] ?? null, resultado.colunas[iPrimeiraMedida]?.tipo ?? 'inteiro')}
                        </strong>
                        {iMedidas.length > 1 && (
                            <span className="rl-cartao-s">
                                {iMedidas.slice(1).map(i => (
                                    <span key={i}>
                                        {resultado.colunas[i].nome}: {formatarValor(linha[i] ?? null, resultado.colunas[i].tipo)}
                                    </span>
                                ))}
                            </span>
                        )}
                    </div>
                ))}
                {resultado.totais.length > 0 && iPrimeiraMedida >= 0 && (
                    <div className="rl-cartao is-total">
                        <span className="rl-cartao-l">Total</span>
                        <strong className="rl-cartao-v">
                            {formatarValor(resultado.totais[iPrimeiraMedida] ?? null, resultado.colunas[iPrimeiraMedida].tipo)}
                        </strong>
                    </div>
                )}
            </div>
        );
    }

    /* ─── Barras ─────────────────────────────────────────────────
       Horizontais e em HTML: a escala é comparativa e o rótulo cabe inteiro,
       o que um eixo vertical de nomes de pessoa não permite. Uma cor só, do
       ChartKit — cor de barra segue a entidade, nunca a magnitude. */
    if (visual === 'barras' && temDimensao && iPrimeiraMedida >= 0) {
        const maior = Math.max(...resultado.linhas.map(l => Number(l[iPrimeiraMedida] ?? 0)), 0);
        return (
            <div className="rl-barras">
                {resultado.linhas.map((linha, n) => {
                    const valor = Number(linha[iPrimeiraMedida] ?? 0);
                    const largura = maior > 0 ? Math.max((valor / maior) * 100, valor > 0 ? 2 : 0) : 0;
                    const rotulo = iDimensoes.map(i => linha[i] ?? '—').join(' · ');
                    return (
                        <div key={n} className="rl-barra">
                            <span className="rl-barra-l" title={rotulo}>{rotulo}</span>
                            <span className="rl-barra-t">
                                <i style={{ width: `${largura}%`, backgroundColor: tokens.primary }} />
                            </span>
                            <span className="rl-barra-v">
                                {formatarValor(valor, resultado.colunas[iPrimeiraMedida].tipo)}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }

    /* ─── Linha ──────────────────────────────────────────────────
       Só existe com separação por tempo, e o servidor já devolve em ordem
       cronológica. Uma linha por medida. */
    if (visual === 'linha' && temDimensao) {
        /* Duas medidas de grandezas diferentes no mesmo eixo achatam a menor contra o
           zero: "vendas fechadas" (10 a 22) vira uma reta no chão ao lado de
           "faturamento" (38 mil a 88 mil). Por isso a segunda medida em diante ganha o
           eixo da direita — e a cor do eixo diz de quem ele é. */
        const temSegundoEixo = iMedidas.length > 1;

        /* A linha sem data não tem lugar numa linha do tempo: desenhada no fim da
           curva, ela sugere uma queda que não existe — foi o que aconteceu com os 55
           negócios sem fechamento, que puxavam o gráfico a zero depois de setembro.
           O dado não é descartado (a regra continua valendo): ele sai da curva e vira
           uma frase embaixo, que é onde a informação não distorce o desenho. */
        const linhaSemData = serie.find(p => p.rotulo === SEM_DATA);
        const serieTempo = serie.filter(p => p.rotulo !== SEM_DATA);

        /* Só vale avisar quando existe valor de fato. Com "vendas fechadas", a linha
           sem data soma zero por definição — um negócio sem fechamento não é uma venda
           fechada —, e a frase viraria "0 ficou fora", que não informa nada. */
        const colunaNota = resultado.colunas[iMedidas[0]];
        const valorSemData = linhaSemData ? Number(linhaSemData[colunaNota.id] ?? 0) : 0;
        const avisarSemData = valorSemData > 0;

        return (
            <div className="rl-gr" ref={refGrafico}>
                <ResponsiveContainer width="100%" height={Math.max(alturaGrafico, alturaMinima)}>
                    <LineChart data={serieTempo} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                        <XAxis dataKey="rotulo" tickLine={false} axisLine={{ stroke: tokens.grid }}
                            tick={{ fill: tokens.axis, fontSize: 12 }} />
                        <YAxis yAxisId="e" tickLine={false} axisLine={false} width={50}
                            tick={{ fill: temSegundoEixo ? tokens.primary : tokens.axis, fontSize: 12 }}
                            tickFormatter={eixoY(iMedidas[0] ?? -1)} />
                        {temSegundoEixo && (
                            <YAxis yAxisId="d" orientation="right" tickLine={false} axisLine={false} width={54}
                                tick={{ fill: tokens.secondary, fontSize: 12 }}
                                tickFormatter={eixoY(iMedidas[1])} />
                        )}
                        <Tooltip {...tooltip} formatter={formatarTooltip} />
                        {iMedidas.map((i, n) => (
                            <Line
                                key={resultado.colunas[i].id}
                                yAxisId={n === 0 ? 'e' : 'd'}
                                type="monotone"
                                dataKey={resultado.colunas[i].id}
                                name={resultado.colunas[i].nome}
                                stroke={n === 0 ? tokens.primary : tokens.secondary}
                                strokeWidth={2}
                                dot={{ r: 3, fill: n === 0 ? tokens.primary : tokens.secondary }}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>

                {avisarSemData && (
                    <p className="rl-gr-nota">
                        Fora da linha: {formatarValor(valorSemData, colunaNota.tipo)} em{' '}
                        {colunaNota.nome.toLowerCase()} sem data, que não tem posição no tempo.
                    </p>
                )}
            </div>
        );
    }

    /* ─── Pizza ──────────────────────────────────────────────────
       Composição de um total. A rampa carrega a ordem das fatias — é o caso em
       que a cor precisa dizer algo que o tamanho já diz mal em fatias pequenas. */
    if (visual === 'pizza' && temDimensao && iPrimeiraMedida >= 0) {
        /* A rampa vai do quase-branco (#dfecff) ao navy, e o resultado já vem ordenado
           da maior fatia para a menor: usada na ordem natural, a fatia dominante ficava
           quase invisível contra o fundo branco. Invertida, o peso da cor acompanha o
           peso do dado. */
        const cores = [...tokens.ramp].reverse();
        const dados = resultado.linhas.map((linha, n) => ({
            nome: String(linha[iDimensoes[0]] ?? '—'),
            valor: Number(linha[iPrimeiraMedida] ?? 0),
            cor: cores[Math.min(n, cores.length - 1)],
        })).filter(d => d.valor > 0);

        const total = dados.reduce((a, d) => a + d.valor, 0);

        return (
            <div className="rl-pizza">
                <ResponsiveContainer width="100%" height={Math.max(140, Math.min(240, alturaMinima))}>
                    <PieChart>
                        <Pie data={dados} dataKey="valor" nameKey="nome" innerRadius={52} outerRadius={88}
                            paddingAngle={1} stroke={tokens.surface} strokeWidth={2}>
                            {dados.map((d, n) => <Cell key={n} fill={d.cor} />)}
                        </Pie>
                        <Tooltip {...tooltip}
                            formatter={(v: number, nome: string) => [
                                `${formatarValor(v, resultado.colunas[iPrimeiraMedida].tipo)} · ${Math.round((v / total) * 100)}%`,
                                nome,
                            ]} />
                    </PieChart>
                </ResponsiveContainer>
                <ul className="rl-legenda">
                    {dados.map((d, n) => (
                        <li key={n}>
                            <i style={{ backgroundColor: d.cor }} />
                            <span className="rl-legenda-n">{d.nome}</span>
                            <span className="rl-legenda-v">
                                {formatarValor(d.valor, resultado.colunas[iPrimeiraMedida].tipo)}
                            </span>
                            <span className="rl-legenda-p">{Math.round((d.valor / total) * 100)}%</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    /* ─── Celular: a tabela vira lista ───────────────────────────
       Decisão dele em 30/09/2026, contra a minha recomendação de manter a
       tabela rolando com a primeira coluna fixa. Uma tabela de 5 colunas mede
       800px e a tela útil tem 358: rolar de lado para ler o valor de cada
       linha não é leitura, é garimpo. Na lista cada registro vira um bloco —
       a identidade em cima, e os números como pares rótulo/valor.

       O preço assumido: perde-se comparar uma coluna inteira de cima a baixo
       com o olho. É o que ele escolheu, sabendo disso.

       Como é o bloco genérico de tabela do relatório, isto vale de uma vez
       para "Vendas do período", "Desempenho por vendedor", "Quem está
       ligando" e para qualquer relatório novo que use tabela. */
    if (celular) {
        /* A identidade é a primeira coluna de texto; sem nenhuma (relatório só
           de números), a própria primeira coluna serve de título. */
        const iTitulo = resultado.colunas.findIndex(c => !ehNumerica(c.tipo));
        const iIdent = iTitulo >= 0 ? iTitulo : 0;

        return (
            <ul className="rl-lista">
                {resultado.linhas.map((linha, n) => (
                    <li key={n} className="rl-lista-item">
                        <span className="rl-lista-t">
                            {formatarValor(linha[iIdent], resultado.colunas[iIdent].tipo) || '—'}
                        </span>
                        <span className="rl-lista-pares">
                            {linha.map((valor, j) => j === iIdent ? null : (
                                <span key={j} className="rl-par">
                                    <i>{resultado.colunas[j].nome}</i>
                                    <b className={ehNumerica(resultado.colunas[j].tipo) ? 'is-num' : ''}>
                                        {formatarValor(valor, resultado.colunas[j].tipo)}
                                    </b>
                                </span>
                            ))}
                        </span>
                    </li>
                ))}
                {temDimensao && resultado.totais.length > 0 && (
                    <li className="rl-lista-item is-total">
                        <span className="rl-lista-t">Total</span>
                        <span className="rl-lista-pares">
                            {resultado.colunas.map((c, i) => i === iIdent ? null : (
                                <span key={c.id} className="rl-par">
                                    <i>{c.nome}</i>
                                    <b className={ehNumerica(c.tipo) ? 'is-num' : ''}>
                                        {formatarValor(resultado.totais[i] ?? null, c.tipo)}
                                    </b>
                                </span>
                            ))}
                        </span>
                    </li>
                )}
            </ul>
        );
    }

    /* ─── Tabela: o formato que sempre serve ─────────────────────── */
    return (
        <div className="rl-tabela-wrap">
            <table className="rl-tabela">
                <thead>
                    <tr>
                        {resultado.colunas.map(c => (
                            <th key={c.id} className={ehNumerica(c.tipo) ? 'is-num' : ''}>{c.nome}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {resultado.linhas.map((linha, n) => (
                        <tr key={n}>
                            {linha.map((valor, j) => (
                                <td key={j} className={ehNumerica(resultado.colunas[j].tipo) ? 'is-num' : ''}>
                                    {formatarValor(valor, resultado.colunas[j].tipo)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                {temDimensao && resultado.totais.length > 0 && (
                    <tfoot>
                        <tr>
                            {resultado.colunas.map((c, i) => (
                                <td key={c.id} className={ehNumerica(c.tipo) ? 'is-num' : ''}>
                                    {i === 0 && !ehNumerica(c.tipo) ? 'Total' : formatarValor(resultado.totais[i] ?? null, c.tipo)}
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
